import React, {useRef, useEffect, useCallback, useState, useMemo} from 'react';
import styled, {keyframes} from 'styled-components';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {Icon, genericMessageHandler} from 'shared/helpers';
import {useAppDispatch, store} from 'store/customer';
import {
    setCabinetPositioningFlags,
    resolveLShapedLeftDepthMm,
    resolveLShapedRightDepthMm,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import {
    invalidateParticularRoomAndCost,
    useGetQuickProductsQuery,
    invalidateJob,
} from 'components/customer/Product/store/productApi';
import {useJobContext} from 'contexts/JobContext';
import {
    RoomPlannerProductWithTemplate,
    CabinetMesh,
    UndoActionContext,
} from 'components/customer/RoomPlanner/types';
import {useNotificationContext} from 'contexts/NotificationContext';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {PartialRoom} from 'shared/types/PartialRoom';
import ProductStyle from 'IndexedDB/models/ProductStyle';
import IDBProduct from 'IndexedDB/models/Product';
import {getProductStyles} from 'service/ProductService';
import {useAddProductManually} from 'shared/helpers/addProductManually';
import {Product} from 'components/customer/Product/entity/Product';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import {quickAppendProductTo3dPreview} from 'components/customer/RoomPlanner/helpers/quickAppendProductTo3dPreview';
import {
    CabinetGroup,
    hasDoors,
} from 'components/customer/RoomPlanner/helpers/doorDetection';
import {dispatchCabinetAddToHistory} from 'components/customer/RoomPlanner/helpers/history/cabinetAddAction';
import {formatHistoryTitlePrefix} from 'components/customer/RoomPlanner/helpers/history/formatHistoryTitle';
import * as THREE from 'three';
import {getProductParams} from 'hooks/preCacheProducts';

interface AddCabinetPopupProps {
    position: {x: number; y: number};
    side: 'left' | 'right'; // Side for content/distance (may be swapped for left/back directions)
    positioningSide: 'left' | 'right'; // Original clicked side for positioning (not swapped)
    distance: number | null; // Distance in scaled units (0.2 = 1mm)
    onClose: () => void;
    onAddFromCatalog?: () => void;
    onAddFromExisting?: () => void;
    scene?: THREE.Scene;
}

type AddProductSaveResult = {
    response?: {
        data?: {
            id?: number;
        };
    };
};

const PopupContainer = styled.div`
    position: fixed;
    background: #1e1e1e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    border-radius: 4px;
    padding: 3px;
    width: 150px;
    z-index: 2000;
    color: #f1f1f1;
`;

const CloseButton = styled.button`
    position: absolute;
    top: -4px;
    right: -8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #444;
    border: none;
    color: #fff;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background: #555;
    }
`;

const Title = styled.div`
    font-size: 14px;
    color: #aaa;
    margin-bottom: 4px;
    text-align: center;
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
`;

const ActionButton = styled.button`
    background: transparent;
    border: none;
    border-radius: 0;
    color: #f1f1f1;
    width: auto;
    min-width: 60px;
    max-width: 70px;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-weight: normal;
    font-size: 12px;
    padding: 2px;
    gap: 2px;
    transition: all 0.2s;
    text-align: center;
    flex: 0 0 auto;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;

    &:hover:not(:disabled) {
        opacity: 0.8;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ButtonIcon = styled(Icon)`
    max-width: 28px;
    max-height: 28px;
`;

const CircularIconWrapper = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #ffffff;
    padding: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const ProductItemImageWrapper = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #ffffff;
    padding: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const ProductItemImage = styled.img`
    width: 34px;
    height: 34px;
    object-fit: contain;
    flex-shrink: 0;
`;

const BackButton = styled.button`
    position: absolute;
    top: 3px;
    left: 3px;
    background: transparent;
    border: none;
    color: #aaa;
    padding: 0;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    z-index: 1;

    &:hover {
        color: #f1f1f1;
    }
`;

const ProductList = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3px;
`;

const ProductItem = styled.button`
    background: transparent;
    border: none;
    border-radius: 0;
    color: #f1f1f1;
    padding: 1px;
    font-size: 10px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;

    &:hover:not(:disabled) {
        opacity: 0.8;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ProductItemContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    text-align: center;
`;

// Skeleton animation
const skeletonLoading = keyframes`
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
`;

// Skeleton components
const SkeletonCircle = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
    background-size: 200% 100%;
    animation: ${skeletonLoading} 1.5s ease-in-out infinite;
`;

const SkeletonText = styled.div`
    height: 12px;
    border-radius: 4px;
    background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
    background-size: 200% 100%;
    animation: ${skeletonLoading} 1.5s ease-in-out infinite;
    margin-top: 4px;
`;

const SkeletonButton = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 60px;
    max-width: 70px;
`;

const SkeletonProductItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 1px;
`;

// Cabinet style IDs (from ProductType and constants)
const BASE_CABINET_STYLE = 1;
const TALL_CABINET_STYLE = 2;
const UPPER_CABINET_STYLE = 3;
const PANTRY_CABINET_STYLE = 4;

// Applied Panels subStyleId for each cabinet style
const getAppliedPanelsSubStyleId = (
    cabinetStyle: number
): number | undefined => {
    switch (cabinetStyle) {
        case BASE_CABINET_STYLE:
            return 2;
        case TALL_CABINET_STYLE:
            return 42;
        case UPPER_CABINET_STYLE:
            return 43;
        case PANTRY_CABINET_STYLE:
            return 44;
        default:
            return undefined;
    }
};

// Helper function to check if product name contains a keyword (case-insensitive)
// Handles both string and array cases
// Uses some() for arrays for better performance (returns true as soon as match is found)
const productNameContains = (
    productName: string | string[] | null | undefined,
    keyword: string
): boolean => {
    if (!productName) return false;

    const lowerKeyword = keyword.toLowerCase();

    // Handle array case - use some() for better performance
    if (Array.isArray(productName)) {
        return productName.some(
            (x) =>
                typeof x === 'string' && x.toLowerCase().includes(lowerKeyword)
        );
    }

    // Handle string case
    if (typeof productName === 'string') {
        return productName.toLowerCase().includes(lowerKeyword);
    }

    return false;
};

const getProductDisplayName = (product: IDBProduct): string => {
    const productRecord = product as unknown as {
        changed_name?: unknown;
        text?: unknown;
    };

    if (typeof productRecord.changed_name === 'string') {
        return productRecord.changed_name;
    }

    if (typeof productRecord.text === 'string') {
        return productRecord.text;
    }

    return '';
};

type ViewMode = 'categories' | 'products';

const AddCabinetPopup: React.FC<AddCabinetPopupProps> = ({
    position,
    positioningSide,
    distance,
    onClose,
    onAddFromCatalog,
}) => {
    const {selectedCabinetGroup, cabinetGroupListRef} = useRoomPlannerContext();
    const popupRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const {room} = useJobContext() as {room: PartialRoom};
    const {notify} = useNotificationContext();
    const addProductManually = useAddProductManually();
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('categories');
    const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(
        null
    );
    const [productStyles, setProductStyles] = useState<ProductStyle[]>([]);

    // Fetch all quick products
    const {data: allProducts = [], isLoading: productsLoading} =
        useGetQuickProductsQuery(getProductParams({room}));

    // Create a Map for O(1) product lookups by ID
    const productsById = useMemo(() => {
        const map = new Map<number, IDBProduct>();
        allProducts.forEach((product) => {
            map.set(product.id, product);
        });
        return map;
    }, [allProducts]);

    // Load product styles on mount
    useEffect(() => {
        const loadProductStyles = async () => {
            try {
                const styles = await getProductStyles();
                setProductStyles(styles);
            } catch (error) {
                // Failed to load product styles - will show no categories
                setProductStyles([]);
                genericMessageHandler(notify, {
                    message: `Failed to load product styles: ${error instanceof Error ? error.message : 'Unknown error'}`,
                });
            }
        };
        void loadProductStyles();
    }, [notify]);
    // Get the selected cabinet's product and extract its type, style, and substyle
    const selectedCabinetProduct = useMemo(() => {
        if (!selectedCabinetGroup || !productsById.size) return null;

        const productTypeId = selectedCabinetGroup.userData.values?.type;
        if (!productTypeId) return null;

        return productsById.get(productTypeId) ?? null;
    }, [selectedCabinetGroup, productsById]);

    // Extract style from selected product (memoized to avoid repeated null checks)
    const selectedCabinetStyle = useMemo(() => {
        return selectedCabinetProduct?.style ?? null;
    }, [selectedCabinetProduct]);

    // Get "Applied Panels" subcategory for the selected cabinet style
    const appliedPanelsSubCategory = useMemo(() => {
        if (selectedCabinetStyle === null || !productStyles.length) return null;

        const styleData = productStyles.find(
            (s) => s.id === selectedCabinetStyle
        );

        // Map cabinet styles to their Applied Panels subStyleId
        const targetSubStyleId =
            getAppliedPanelsSubStyleId(selectedCabinetStyle);
        if (!targetSubStyleId) return null;

        return styleData?.subStyles?.find(
            (sub) => sub.subStyleId === targetSubStyleId
        );
    }, [selectedCabinetStyle, productStyles]);

    // Get all Applied Panels products for the selected cabinet style
    const appliedPanelsProducts = useMemo(() => {
        if (
            selectedCabinetStyle === null ||
            !appliedPanelsSubCategory ||
            !allProducts.length
        )
            return [];

        return allProducts.filter((product) => {
            return (
                product.style === selectedCabinetStyle &&
                product.subStyleId === appliedPanelsSubCategory.subStyleId
            );
        });
    }, [allProducts, selectedCabinetStyle, appliedPanelsSubCategory]);

    // Group Applied Panels products by type (Panel, Vermin, Filler)
    // Filter each product individually based on its minWidth requirement
    const productGroups = useMemo(() => {
        const groups: {
            [key: string]: {
                name: string;
                products: IDBProduct[];
                iconName: string;
            };
        } = {
            panel: {
                name: 'Panel',
                products: [],
                iconName: 'Quick-Panel.svg',
            },
            filler: {
                name: 'Filler',
                products: [],
                iconName: 'Filler Button.svg',
            },
            vermin: {
                name: 'Vermin',
                products: [],
                iconName: 'Vermin Filler Button.svg',
            },
        };

        // Convert distance from scaled units to mm for comparison
        // Distance is in scaled units (0.2 = 1mm), so to convert to mm: distance / 0.2
        const scale = 0.2;
        const distanceInMm = distance !== null ? distance / scale : Infinity;

        // Minimum width for Panel products (default 20mm if not specified)
        const PANEL_MIN_WIDTH_MM = 19;

        appliedPanelsProducts.forEach((product) => {
            const productName = getProductDisplayName(product);

            // Determine product type once (avoid multiple productNameContains calls)
            const isPanel = productNameContains(productName, 'panel');
            const isFiller = productNameContains(productName, 'filler');
            const isVermin = productNameContains(productName, 'vermin');

            // Get product's minWidth in mm
            const productMinWidth = Number(product.minWidth) || 0;

            // Determine if this product should be shown based on distance
            let shouldShow = false;

            if (distance === null || distance === undefined) {
                // Infinite space - show all products
                shouldShow = true;
            } else {
                // Check if distance meets the product's minimum width requirement
                if (isPanel) {
                    // For panels: if no minWidth specified, show when distance >= 20mm
                    shouldShow = distanceInMm >= PANEL_MIN_WIDTH_MM;
                } else if (isFiller || isVermin) {
                    // For fillers and vermin, use the product's minWidth (must be > 0)
                    shouldShow =
                        productMinWidth > 0 && distanceInMm >= productMinWidth;
                } else {
                    // Unknown product type - show by default
                    shouldShow = true;
                }
            }

            // Only add product if it meets the distance requirement
            if (shouldShow) {
                if (isFiller) {
                    groups.filler.products.push(product);
                } else if (isVermin) {
                    groups.vermin.products.push(product);
                } else if (isPanel) {
                    groups.panel.products.push(product);
                }
            }
        });

        // Filter out groups that have no products after filtering
        const filteredGroups = Object.entries(groups).filter(
            ([, group]) => group.products.length > 0
        );

        return filteredGroups.map(([key, group]) => ({key, ...group}));
    }, [appliedPanelsProducts, distance]);

    // Filter products by selected group
    const filteredProducts = useMemo(() => {
        if (selectedGroupKey === null || !productGroups.length) return [];

        const group = productGroups.find((g) => g.key === selectedGroupKey);
        return group?.products || [];
    }, [productGroups, selectedGroupKey]);

    // Check if we're still in initial loading state
    // Show skeletons if products are loading, or if we don't have data yet
    const isInitialLoading = useMemo(() => {
        return (
            productsLoading ||
            allProducts.length === 0 ||
            productStyles.length === 0
        );
    }, [productsLoading, allProducts.length, productStyles.length]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    // Close when cabinet is deselected
    useEffect(() => {
        if (!selectedCabinetGroup) {
            onClose();
        }
    }, [selectedCabinetGroup, onClose]);

    // Handle group selection (Panel, Filler, Vermin)
    const handleGroupClick = useCallback((groupKey: string): void => {
        setSelectedGroupKey(groupKey);
        setViewMode('products');
    }, []);

    // Create group click handler
    const createGroupClickHandler = useCallback(
        (groupKey: string) => () => {
            handleGroupClick(groupKey);
        },
        [handleGroupClick]
    );

    // Handle back to categories
    const handleBackToCategories = useCallback(() => {
        setViewMode('categories');
        setSelectedGroupKey(null);
    }, []);

    // Handle adding a specific product
    const handleAddProduct = useCallback(
        async (
            product: IDBProduct,
            ignoreHistoryDispatch = false,
            historyCtx?: UndoActionContext
        ) => {
            if (!selectedCabinetGroup || !room || loading) return;

            setLoading(true);
            let placeholderMeshes: CabinetMesh[] = [];

            try {
                // Get dimensions from selected cabinet to inherit height and depth
                const selectedCabinetValues = selectedCabinetGroup.userData
                    .values as RoomPlannerProductWithTemplate;

                const sitsOnFloor = Boolean(
                    selectedCabinetGroup.userData.sitOnFloor
                );
                const toeKickHeight =
                    Number(
                        selectedCabinetGroup.userData.values.cabinet_toekick
                    ) || 0;
                const cabinetHeight =
                    Number(selectedCabinetValues.cabinet_height) || 0;

                // Calculate inherited height: subtract toe kick height if cabinet sits on floor
                const inheritedHeight = sitsOnFloor
                    ? cabinetHeight - toeKickHeight
                    : cabinetHeight;

                // L-shaped cabinets have per-wing depths. Side-button panels/vermin
                // must match the wing they attach to (left → leftDepth, right → rightDepth).
                const isLShapedProduct = Boolean(
                    selectedCabinetGroup.userData.isLShapedProduct
                );
                let inheritedDepth = isLShapedProduct
                    ? positioningSide === 'left'
                        ? resolveLShapedLeftDepthMm(selectedCabinetValues)
                        : resolveLShapedRightDepthMm(selectedCabinetValues)
                    : Number(selectedCabinetValues.cabinet_depth);
                const cabinetHasDoors = hasDoors(
                    selectedCabinetGroup as CabinetGroup
                );
                if (cabinetHasDoors) {
                    inheritedDepth += 20;
                }

                // Set positioning flags so the new cabinet positions next to the selected one
                // Use positioningSide (original clicked side) not the swapped side
                setCabinetPositioningFlags(
                    selectedCabinetGroup,
                    positioningSide
                );

                // Use minWidth from product (addProductManually will fetch full product data internally)
                const minWidth = Number(product.minWidth) || 80;

                // Create placeholder mesh BEFORE saving to show immediate feedback
                try {
                    await quickAppendProductTo3dPreview(
                        product, // IDBProduct has id property, compatible with ItemType | {id: number}
                        1, // quantity
                        minWidth, // width
                        room,
                        cabinetGroupListRef,
                        {
                            depth: inheritedDepth,
                            height: inheritedHeight,
                        }
                    );

                    // Track placeholder meshes for cleanup if save fails
                    placeholderMeshes = cabinetGroupListRef.current.filter(
                        (mesh) => mesh.userData.isQuickAddPending === true
                    );
                } catch (placeholderError) {
                    // If placeholder creation fails, show error but continue with save
                    // The placeholder is optional, so we don't throw here
                    genericMessageHandler(notify, {
                        message: `Failed to add the product: ${placeholderError instanceof Error ? placeholderError.message : 'Unknown error'}`,
                    });
                }

                // Save product with inherited dimensions
                const saveResult: AddProductSaveResult =
                    await addProductManually({
                        product: {
                            id: (product as {id: number}).id,
                        } as Partial<Product>,
                        room,
                        width: minWidth,
                        height: inheritedHeight,
                        depth: inheritedDepth,
                        cabinetData: {
                            cabinet_extend: sitsOnFloor,
                        },
                    });

                if ('error' in saveResult) {
                    // Clean up placeholder meshes if save failed
                    if (placeholderMeshes.length > 0 && cabinetGroupListRef) {
                        placeholderMeshes.forEach((mesh) => {
                            deleteCabinetMesh(mesh, false, cabinetGroupListRef);
                        });
                    }
                    const errorMessage =
                        (saveResult as {error?: {data?: {error?: string}}})
                            .error?.data?.error ?? 'Failed to save product';
                    throw new Error(
                        `${errorMessage}. Please try adding the product manually.`
                    );
                }

                genericMessageHandler(
                    notify,
                    {
                        message: `${getProductDisplayName(product)} added successfully`,
                    },
                    'success'
                );

                // Invalidate counts
                dispatch(invalidateTotalProductCount());
                dispatch(invalidateParticularRoomAndCost(Number(room.id)));
                dispatch(invalidateJob());

                const createdId = Number(saveResult.response?.data?.id);

                // Always inform history (undo/redo) about the real created id, even on redo
                if (historyCtx?.setRestoredCabinetId && createdId) {
                    historyCtx.setRestoredCabinetId(createdId);
                }

                // But only push a new history entry on the initial add (not during redo)
                if (!ignoreHistoryDispatch && saveResult.response?.data?.id) {
                    const roomIndex = store.getState().roomPlanner.roomIndex;
                    const data = saveResult.response?.data as
                        {id?: number; room_cab_number?: number} | undefined;
                    const roomCabNumber = data?.room_cab_number;
                    const productName = getProductDisplayName(product);

                    dispatchCabinetAddToHistory(dispatch, {
                        title: `**Add** ${formatHistoryTitlePrefix(roomIndex, roomCabNumber)} ${productName} From Floating Menu`.trimEnd(),
                        data: {
                            id: createdId,
                            redoAction: (ctx) =>
                                void handleAddProduct(product, true, ctx),
                        },
                    });
                }

                onAddFromCatalog?.();
                onClose();
            } catch (err) {
                // Clean up placeholder meshes on any error
                if (placeholderMeshes.length > 0 && cabinetGroupListRef) {
                    placeholderMeshes.forEach((mesh) => {
                        deleteCabinetMesh(mesh, false, cabinetGroupListRef);
                    });
                }
                const originalError =
                    err instanceof Error ? err.message : 'Unknown error';
                const errorMessage = originalError.includes(
                    'Please try adding the product manually.'
                )
                    ? originalError
                    : `${originalError}. Please try adding the product manually.`;
                genericMessageHandler(notify, {
                    message: errorMessage,
                });
                onClose();
            } finally {
                setLoading(false);
            }
        },
        [
            selectedCabinetGroup,
            room,
            loading,
            positioningSide,
            notify,
            dispatch,
            addProductManually,
            onAddFromCatalog,
            onClose,
            cabinetGroupListRef,
        ]
    );

    // Create product click handler
    const createProductClickHandler = useCallback(
        (product: IDBProduct) => () => {
            void handleAddProduct(product);
        },
        [handleAddProduct]
    );

    // Handle image error
    const handleImageError = useCallback(
        (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            e.currentTarget.style.display = 'none';
        },
        []
    );

    return (
        <PopupContainer
            ref={popupRef}
            style={{
                top: `${position.y}px`,
                left: `${position.x}px`,
            }}>
            <CloseButton onClick={onClose}>×</CloseButton>
            {viewMode === 'products' && (
                <BackButton onClick={handleBackToCategories}>←</BackButton>
            )}
            {/* <Title>Add to {side === 'left' ? 'Left' : 'Right'}</Title> */}
            <Title>Quick Add :</Title>

            {viewMode === 'categories' ? (
                // Show product groups (Panel, Filler, Vermin) from Applied Panels
                <ButtonsContainer>
                    {isInitialLoading ? (
                        <>
                            <SkeletonButton>
                                <SkeletonCircle />
                                <SkeletonText style={{width: '50px'}} />
                            </SkeletonButton>
                            <SkeletonButton>
                                <SkeletonCircle />
                                <SkeletonText style={{width: '50px'}} />
                            </SkeletonButton>
                        </>
                    ) : productGroups.length === 0 ? (
                        <div
                            style={{
                                padding: '8px',
                                textAlign: 'center',
                                color: '#aaa',
                                fontSize: '12px',
                                width: '100%',
                            }}>
                            Not enough space to add a product
                        </div>
                    ) : (
                        productGroups.map((group) => {
                            const count = group.products.length;
                            return (
                                <ActionButton
                                    key={group.key}
                                    onClick={createGroupClickHandler(group.key)}
                                    disabled={
                                        loading ||
                                        productsLoading ||
                                        count === 0
                                    }
                                    title={`${count} ${group.name} product${count !== 1 ? 's' : ''} available`}>
                                    <CircularIconWrapper>
                                        <ButtonIcon
                                            color="#204381"
                                            iconName={group.iconName}
                                        />
                                    </CircularIconWrapper>
                                    {group.name}
                                    {count > 0 ? ` (${count})` : ''}
                                </ActionButton>
                            );
                        })
                    )}
                </ButtonsContainer>
            ) : (
                // Show product list for selected category
                <ProductList>
                    {isInitialLoading ? (
                        <>
                            <SkeletonProductItem>
                                <SkeletonCircle
                                    style={{width: '40px', height: '40px'}}
                                />
                                <SkeletonText
                                    style={{width: '60px', height: '10px'}}
                                />
                            </SkeletonProductItem>
                            <SkeletonProductItem>
                                <SkeletonCircle
                                    style={{width: '40px', height: '40px'}}
                                />
                                <SkeletonText
                                    style={{width: '60px', height: '10px'}}
                                />
                            </SkeletonProductItem>
                            <SkeletonProductItem>
                                <SkeletonCircle
                                    style={{width: '40px', height: '40px'}}
                                />
                                <SkeletonText
                                    style={{width: '60px', height: '10px'}}
                                />
                            </SkeletonProductItem>
                            <SkeletonProductItem>
                                <SkeletonCircle
                                    style={{width: '40px', height: '40px'}}
                                />
                                <SkeletonText
                                    style={{width: '60px', height: '10px'}}
                                />
                            </SkeletonProductItem>
                        </>
                    ) : filteredProducts.length === 0 ? (
                        <div
                            style={{
                                padding: '8px',
                                textAlign: 'center',
                                color: '#aaa',
                            }}>
                            No products found
                        </div>
                    ) : (
                        filteredProducts.map((product) => {
                            const productName = getProductDisplayName(product);
                            const productImage =
                                product.changedImage || product.image;

                            // Determine icon or image to use (check once per product)
                            let iconName: string | null = null;
                            const lowerName = productName.toLowerCase();
                            if (lowerName.includes('vermin')) {
                                iconName = 'Vermin Filler Button.svg';
                            } else if (lowerName.includes('filler')) {
                                iconName = 'Filler Button.svg';
                            } else if (lowerName.includes('panel')) {
                                iconName = 'Quick-Panel.svg';
                            }

                            return (
                                <ProductItem
                                    key={product.id}
                                    onClick={createProductClickHandler(product)}
                                    disabled={loading}>
                                    <ProductItemImageWrapper>
                                        {iconName ? (
                                            <ButtonIcon
                                                color="#204381"
                                                iconName={iconName}
                                            />
                                        ) : productImage ? (
                                            <ProductItemImage
                                                src={productImage}
                                                alt={productName}
                                                onError={handleImageError}
                                            />
                                        ) : null}
                                    </ProductItemImageWrapper>
                                    <ProductItemContent>
                                        <div>
                                            <strong>{productName}</strong>
                                        </div>
                                    </ProductItemContent>
                                </ProductItem>
                            );
                        })
                    )}
                </ProductList>
            )}
        </PopupContainer>
    );
};

export default AddCabinetPopup;
